import { supabase } from "../../supabase/supabase";

// The call_diagnosis_* tables are not in the generated src/types/supabase.ts yet, so the row
// types live here.

export type DiagnosisStatus = "running" | "done" | "error";
export type DiagnosisVerdict = "correct" | "wrong";

export interface DiagnosisStep {
  label: string;
  detail?: string;
  at?: string;
}

export interface DiagnosisFeedbackRow {
  id: string;
  message_id: string;
  user_id: string;
  user_email: string | null;
  verdict: DiagnosisVerdict;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface DiagnosisMessageRow {
  id: string;
  thread_id: string;
  role: "user" | "assistant";
  text: string | null;
  steps: DiagnosisStep[];
  status: DiagnosisStatus;
  error: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  cost_usd: number | string;
  created_by: string | null;
  created_by_email: string | null;
  created_at: string;
  updated_at: string;
  feedback?: DiagnosisFeedbackRow[];
}

export interface DiagnosisThreadSummary {
  id: string;
  call_log_id: string;
  title: string;
  created_by_email: string | null;
  created_at: string;
  updated_at: string;
  messageCount: number;
  // status of the newest assistant answer
  lastStatus: DiagnosisStatus | null;
  correct: number;
  wrong: number;
}

export interface RunDiagnosisInput {
  callLogId: string;
  threadId?: string;
  query: string;
}

export interface DiagnosisDone {
  message_id: string;
  text: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  cost_usd: number;
}

export interface RunDiagnosisHandlers {
  onStart?: (e: { thread_id: string; user_message_id: string; assistant_message_id: string }) => void;
  onStep?: (step: DiagnosisStep) => void;
  onText?: (delta: string) => void;
  onDone?: (e: DiagnosisDone) => void;
  // status is the HTTP status for errors before streaming, undefined for errors inside the stream
  onError?: (message: string, status?: number) => void;
}

const MESSAGE_COLUMNS =
  "id, thread_id, role, text, steps, status, error, input_tokens, output_tokens, cache_read_tokens, cache_write_tokens, cost_usd, created_by, created_by_email, created_at, updated_at";

export const callDiagnosisService = {
  async getCurrentUser(): Promise<{ id: string; email: string | null } | null> {
    const { data } = await supabase.auth.getSession();
    const user = data.session?.user;
    return user ? { id: user.id, email: user.email ?? null } : null;
  },

  async listThreads(callLogId: string): Promise<DiagnosisThreadSummary[]> {
    const { data, error } = await supabase
      .from("call_diagnosis_threads")
      .select(
        "id, call_log_id, title, created_by_email, created_at, updated_at, call_diagnosis_messages(id, role, status, created_at, call_diagnosis_feedback(verdict))",
      )
      .eq("call_log_id", callLogId)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);

    return (data ?? []).map((t: any) => {
      const messages: any[] = t.call_diagnosis_messages ?? [];
      const assistants = messages
        .filter((m) => m.role === "assistant")
        .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
      let correct = 0;
      let wrong = 0;
      for (const m of assistants) {
        for (const f of m.call_diagnosis_feedback ?? []) {
          if (f.verdict === "correct") correct++;
          else if (f.verdict === "wrong") wrong++;
        }
      }
      return {
        id: t.id,
        call_log_id: t.call_log_id,
        title: t.title,
        created_by_email: t.created_by_email,
        created_at: t.created_at,
        updated_at: t.updated_at,
        messageCount: messages.length,
        lastStatus: assistants.length ? assistants[assistants.length - 1].status : null,
        correct,
        wrong,
      } as DiagnosisThreadSummary;
    });
  },

  async getThreadMessages(threadId: string): Promise<DiagnosisMessageRow[]> {
    const { data, error } = await supabase
      .from("call_diagnosis_messages")
      .select(`${MESSAGE_COLUMNS}, feedback:call_diagnosis_feedback(*)`)
      .eq("thread_id", threadId)
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as DiagnosisMessageRow[];
  },

  async getMessage(id: string): Promise<DiagnosisMessageRow | null> {
    const { data, error } = await supabase
      .from("call_diagnosis_messages")
      .select(`${MESSAGE_COLUMNS}, feedback:call_diagnosis_feedback(*)`)
      .eq("id", id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (data as unknown as DiagnosisMessageRow) ?? null;
  },

  async upsertFeedback(
    messageId: string,
    verdict: DiagnosisVerdict,
    note: string | null,
  ): Promise<DiagnosisFeedbackRow> {
    const user = await this.getCurrentUser();
    if (!user) throw new Error("Not signed in");
    const { data, error } = await supabase
      .from("call_diagnosis_feedback")
      .upsert(
        {
          message_id: messageId,
          user_id: user.id,
          user_email: user.email,
          verdict,
          note: note?.trim() ? note.trim() : null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "message_id,user_id" },
      )
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return data as DiagnosisFeedbackRow;
  },

  async deleteFeedback(messageId: string): Promise<void> {
    const user = await this.getCurrentUser();
    if (!user) throw new Error("Not signed in");
    const { error } = await supabase
      .from("call_diagnosis_feedback")
      .delete()
      .eq("message_id", messageId)
      .eq("user_id", user.id);
    if (error) throw new Error(error.message);
  },

  async getDiagnosisCounts(callIds: string[]): Promise<Record<string, number>> {
    if (callIds.length === 0) return {};
    const { data, error } = await supabase.rpc("call_diagnosis_counts", { call_ids: callIds });
    if (error) throw new Error(error.message);
    const counts: Record<string, number> = {};
    for (const row of (data ?? []) as { call_log_id: string; threads: number }[]) {
      counts[row.call_log_id] = Number(row.threads);
    }
    return counts;
  },

  // supabase.functions.invoke cannot stream, so this is a raw fetch. The server keeps running if
  // the signal aborts; the DB row is the source of truth.
  async runDiagnosis(
    input: RunDiagnosisInput,
    handlers: RunDiagnosisHandlers,
    signal?: AbortSignal,
  ): Promise<void> {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        handlers.onError?.("Not signed in", 401);
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/diagnose_call`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          call_log_id: input.callLogId,
          thread_id: input.threadId,
          query: input.query,
        }),
        signal,
      });

      if (!response.ok || !response.body) {
        let message = `Request failed (${response.status})`;
        try {
          const body = await response.json();
          if (body?.error) message = String(body.error);
        } catch {
          // body was not JSON; keep the generic message
        }
        handlers.onError?.(message, response.status);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finished = false;

      const dispatch = (raw: string) => {
        let event = "message";
        const dataLines: string[] = [];
        for (const line of raw.split("\n")) {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
        }
        if (dataLines.length === 0) return;
        let payload: any;
        try {
          payload = JSON.parse(dataLines.join("\n"));
        } catch {
          return;
        }
        if (event === "start") handlers.onStart?.(payload);
        else if (event === "step") handlers.onStep?.(payload);
        else if (event === "text") handlers.onText?.(payload.delta ?? "");
        else if (event === "done") {
          finished = true;
          handlers.onDone?.(payload);
        } else if (event === "error") {
          finished = true;
          handlers.onError?.(payload.message ?? "Diagnosis failed");
        }
      };

      for (;;) {
        const { value, done } = await reader.read();
        if (value) buffer += decoder.decode(value, { stream: !done }).replace(/\r\n/g, "\n");
        // Events end at a blank line; a chunk can split one anywhere.
        let sep = buffer.indexOf("\n\n");
        while (sep !== -1) {
          dispatch(buffer.slice(0, sep));
          buffer = buffer.slice(sep + 2);
          sep = buffer.indexOf("\n\n");
        }
        if (done) break;
      }
      if (buffer.trim()) dispatch(buffer);
      if (!finished) handlers.onError?.("The connection closed before the answer finished.");
    } catch (err) {
      if ((err as any)?.name === "AbortError") return;
      handlers.onError?.(err instanceof Error ? err.message : "Network error");
    }
  },
};
